<template>
  <div class="search-activator">
    <v-expand-x-transition>
      <BrandTextField
        v-if="active || search" ref="input" v-model.trim="search"
        :placeholder="computedPlaceholder" variant="basic"
        :height="minified ? 45 : undefined" :density="minified ? 'compact' : undefined"
        @focus="focused = true"
        @keypress.enter="$refs.input?.blur()"
        @blur="active = false; focused = false"
        rounded
      >
        <template v-if="minified ? focused : true" #prepend-inner>
          <PhMagnifyingGlass
            class="text-h5 ml-1 ml-sm-3 mr-1 mr-sm-2 text-blue400"
          />
        </template>

        <template #append-inner>
          <v-progress-circular
            v-if="loading" color="blue50"
            size="26" width="3" class="mt-1 mr-2"
            indeterminate
          />

          <BrandButton
            v-else-if="showXButton" icon elevation="0" class="bg-transparent" size="small"
            @click="handleXButtonClick"
          >
            <PhXCircle class="text-h5 text-neutral400" weight="fill" />
          </BrandButton>
        </template>
      </BrandTextField>

      <BrandButton
        v-else
        variant="secondary"
        flat
        color="neutral25" class="text-neutral900"
        v-bind="toolbarBtnProps"
        elevation="0"
        @click="onSearch"
      >
        <PhMagnifyingGlass class="text-h5" />
        <span v-if="!isCompact" class="ml-3" v-text="t('cta.search')" />
      </BrandButton>
    </v-expand-x-transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { PhMagnifyingGlass, PhXCircle } from '@phosphor-icons/vue'
import { useToolbarButtonStyle } from '@/composables/useToolbarButtonStyle'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  loading: Boolean,
  minified: Boolean,
  placeholder: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'focused'])

// Store and composables
const store = useStore()
const { t } = useI18n()
const { isCompact, toolbarBtnProps } = useToolbarButtonStyle()

// Refs
const input = ref(null)
const active = ref(false)
const focused = ref(false)
const search = ref(props.modelValue)

// Computed
const isMobile = computed(() => store.getters['device/isMobile'])
const isNative = computed(() => store.getters['device/isNative'])
const isExtension = computed(() => store.getters['device/isExtension'])
const isExtensionPopup = computed(() => store.getters['device/isExtensionPopup'])
const isExtensionMini = computed(() => store.getters['device/isExtensionMini'])

const computedPlaceholder = computed(() => {
  return props.placeholder ?? t('form.field.search.label')
})

const showXButton = computed(() => {
  return isMobile.value || search.value || isNative.value || isExtension.value ||
         isExtensionMini.value || isExtensionPopup.value
})

const handleXButtonClick = () => {
  if (search.value) {
    active.value = true
    search.value = ''
    input.value?.focus()
  } else {
    active.value = false
    focused.value = false
  }
}

const onSearch = async () => {
  active.value = true
  focused.value = true

  await nextTick()
  input.value?.focus()
}

// Watchers
watch(() => props.modelValue, newVal => {
  if (search.value === newVal) return
  search.value = newVal
})

watch(search, newVal => {
  emit('update:modelValue', newVal)
})

watch(focused, newVal => {
  emit('focused', newVal)
})
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as vars;

  .brand-text-field {
    // Fully rounded pill shape for search input
    border-radius: vars.$border-radius-search !important;

    // Override Vuetify and BrandTextField border-radius for all field elements
    :deep(.v-field) {
      border-radius: vars.$border-radius-search !important;
    }

    :deep(.v-field__overlay) {
      border-radius: vars.$border-radius-search !important;
    }

    :deep(.v-field__outline) {
      border-radius: vars.$border-radius-search !important;
    }

    :deep(.v-field__outline__start) {
      border-radius: vars.$border-radius-search 0 0 vars.$border-radius-search !important;
    }

    :deep(.v-field__outline__end) {
      border-radius: 0 vars.$border-radius-search vars.$border-radius-search 0 !important;
    }

    :deep(.v-input__append-inner) {
      margin-top: 10px;
    }
  }

  .expand-x-transition {
    &-leave-active {
      position: absolute;
    }
  }
</style>
